/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import InputText from 'components/Inputs/inputText';
import InputEmail from 'components/Inputs/inputEmail';
import InputSelect from 'components/Inputs/inputSelect';
import Button from 'components/Buttons/Button';
import useDialog from 'components/Dialog/useDialog';
import FormLayout from 'layouts/FormLayout';
import { ROUTE_PATH } from 'routes';
import {
  Link, Navigate, useSearchParams, useNavigate, useLocation,
} from 'react-router-dom';
import { signUp } from '../modules/actions';
import 'routes/Auth/styles.scss';

function Signup(props) {
  const {
    literals,
    platform,
    userLang,
    socialSignup,
  } = props;

  const [valueName, setValueName] = useState('');
  const [valueLastName, setValueLastName] = useState('');
  const [valueEmail, setValueEmail] = useState('');
  const [valueLang, setValueLang] = useState(userLang);
  const [signed, setSigned] = useState(false);
  const [loading, setLoading] = useState(false);
  const { dialog } = useDialog();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const invite = searchParams.get('invite');
  const inviteFundraise = searchParams.get('inviteFundraise');
  const referral = searchParams.get('referral');
  const fundraise = searchParams.get('fundraise');
  const startup = searchParams.get('startup');
  const external = searchParams.get('external');

  const getReferral = () => {
    let auxReferral = null;
    if (inviteFundraise) {
      auxReferral = {
        type: 'inviteFundraise',
        id: inviteFundraise,
        element: fundraise ? { type: 'fundraise', id: fundraise } : null,
      };
    } else if (referral) {
      if (fundraise) {
        auxReferral = {
          type: 'fundraise',
          id: referral,
          element: fundraise ? { type: 'fundraise', id: fundraise } : null,
        };
      } else if (startup) {
        auxReferral = {
          type: 'startup',
          id: referral,
          element: startup ? { type: 'startup', id: startup } : null,
        };
      }
    }
    return auxReferral;
  };

  const goToLogin = {
    pathname: ROUTE_PATH.LOGIN,
    state: location.state,
    search: location.search,
  };

  const handleSocialSignup = async (provider, token) => {
    const params = {
      oauth: {
        type: provider,
        id: token,
      },
      invite: invite || null,
      referral: getReferral(),
    };
    const success = await socialSignup(params);
    if (success) {
      navigate(goToLogin);
    } else {
      await dialog({
        type: 'error',
        text: literals.socialSignupError,
      });
    }
  };

  useEffect(() => {
    if (platform.oauth.google && google) {
      /* global google */
      google.accounts.id.initialize({
        client_id: platform.oauth.google,
        callback: response => handleSocialSignup('google', response.credential),
      });
      google.accounts.id.renderButton(
        document.getElementById('signInDiv'),
        {
          theme: 'outline',
          size: 'large',
          text: literals.login,
          height: 44,
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleValueChange = (value, elem) => {
    if (elem === 'name') {
      setValueName(value);
    }
    if (elem === 'lastName') {
      setValueLastName(value);
    }
    if (elem === 'email') {
      setValueEmail(value);
    }
  };

  const handleLangChange = (value) => {
    setValueLang(value);
  };

  const checkIsDisabled = () => valueName === '' || valueLastName === '' || valueEmail === '';

  const optionsLangs = platform.langs.map((elem) => ({ id: elem, name: elem }));

  const submitForm = async (event) => {
    event.preventDefault();
    const form = {
      firstname: valueName,
      lastname: valueLastName,
      email: valueEmail,
      lang: valueLang,
      role: 'user',
      invite: invite || null,
      referral: getReferral(),
      external,
    };

    setLoading(true);
    try {
      await signUp(form);
      setLoading(false);
      await dialog({
        type: 'success',
        text: literals.signupSuccess,
      });
      setSigned(true);
    } catch (e) {
      setLoading(false);
      await dialog({
        type: 'error',
        text: literals.signupError,
      });
    }
  };

  if (!(platform.properties.signup.public || inviteFundraise || referral)) {
    return (
      <div className='form-wrapper form-auth'>
        <div className='form-box box form-wrapper-notFound'>
          <h4 className='fw-b mb-4 text-center'>{literals.notFound}</h4>
          <div className='goto-login'>
            <Link to={goToLogin}>
              <Button text={literals.goToLogin} color='secondary' />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormLayout title={literals.title} className='form-auth'>
      <form onSubmit={submitForm}>
        <InputText
          preText={literals.firstname}
          name={literals.firstname}
          placeholder={literals.firstname}
          value={valueName}
          maxLength={99999}
          onChange={value => handleValueChange(value, 'name')}
        />
        <InputText
          preText={literals.lastname}
          name={literals.lastname}
          placeholder={literals.lastname}
          value={valueLastName}
          maxLength={99999}
          onChange={value => handleValueChange(value, 'lastName')}
        />
        <InputEmail
          preText={literals.email}
          placeholder={literals.email}
          isRequired
          value={valueEmail}
          onChange={value => handleValueChange(value, 'email')}
        />
        <InputSelect
          preText={literals.language}
          name={literals.language}
          value={valueLang}
          options={optionsLangs}
          onChange={handleLangChange}
        />
        <div className='form-actions'>
          <Button
            text={literals.createAccount}
            type='submit'
            disabled={checkIsDisabled()}
            loading={loading}
          />
          {
            platform.oauth.google && window.google ? (
              <div className='social-actions'>
                You can also sign un with&nbsp;
                <span onClick={google.accounts.id.prompt}>
                  GOOGLE
                </span>
              </div>
            ) : null
          }
          <div className='social-actions'>
            {literals.questionRegistered}
            &nbsp;
            <Link className='link-effect' to={goToLogin}>
              <span>{literals.goToLogin}</span>
            </Link>
          </div>
        </div>
      </form>
      {signed && <Navigate to={goToLogin} />}

    </FormLayout>
  );
}

Signup.propTypes = {
  literals: PropTypes.object.isRequired,
  platform: PropTypes.object.isRequired,
  userLang: PropTypes.string.isRequired,
  socialSignup: PropTypes.func.isRequired,
};

export default Signup;
